import { FC, useMemo, useState } from 'react';
import { Button, Card, RadixTable, TColumn } from '@features/ui';
import { periodOptions } from '@features/admin/reports/reportData';
import {
  ReportPeriod,
  TReportAverageAmountByProductsBreakdownData,
  TReportAverageAmountByProductsBreakdownParams,
} from '@src/types';
import { useReportFilters } from '@hooks/useReportFilters';
import { useProducts } from '@src/hooks/useProducts';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  exportAverageAmountByProductsBreakdown,
  getAverageAmountByProductsBreakdown,
} from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

const downloadCsvFile = (blobPart: BlobPart, fileName: string) => {
  const csvBlob = new Blob([blobPart], { type: 'text/csv;charset=utf-8;' });
  const csvUrl = URL.createObjectURL(csvBlob);
  const link = document.createElement('a');

  link.href = csvUrl;
  link.download = `${fileName}.csv`;
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(csvUrl);
  document.body.removeChild(link);
};

export const AverageAmountByProductsBreakdown: FC = () => {
  const [data, setData] = useState<
    TReportAverageAmountByProductsBreakdownData[]
  >([]);
  const { products } = useProducts();

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    enableComparisons: false,
    enableProducts: true,
  });

  const columns = useMemo<
    TColumn<TReportAverageAmountByProductsBreakdownData>[]
  >(
    () => [
      {
        title: '#',
        render: (item) => (
          <p className="text-white">
            {item?.product_name || item?.product_id || '-'}
          </p>
        ),
      },
      {
        title: 'Average Amount',
        className: 'text-right',
        render: (item) => (
          <p className="text-white">
            $
            {Number(item?.average_amount ?? 0).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </p>
        ),
      },
      {
        title: 'Average Count of Purchases',
        className: 'text-right',
        render: (item) => (
          <p className="text-white">
            {Number(item?.average_count_of_purchases ?? 0).toLocaleString(
              undefined,
              {
                minimumFractionDigits: 3,
                maximumFractionDigits: 3,
              }
            )}
          </p>
        ),
      },
    ],
    []
  );

  const buildReportPayload =
    (): TReportAverageAmountByProductsBreakdownParams => {
      const selectedProductIds = filters.selectedProducts
        .map((item) => Number(item))
        .filter((item) => !Number.isNaN(item));

      return {
        start_date: filters.startDate
          ? dayjs(filters.startDate).format('YYYY-MM-DD')
          : null,
        end_date: filters.endDate
          ? dayjs(filters.endDate).format('YYYY-MM-DD')
          : null,
        product_ids: selectedProductIds.length ? selectedProductIds : undefined,
      };
    };

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildReportPayload();
      const response = await getAverageAmountByProductsBreakdown(payload);

      setData(response);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async (format: 'csv' | 'xlsx') => {
    try {
      const payload = buildReportPayload();
      const response = await exportAverageAmountByProductsBreakdown(
        payload,
        format
      );

      if (format === 'csv') {
        downloadCsvFile(response.data, 'average_amount_by_products_breakdown');
      } else {
        downloadFile(response.data, 'average_amount_by_products_breakdown');
      }

      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Average Amount By Products Breakdown
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showProductsFilter
          dateRangeValue={filters.valueStart}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          productOptions={products}
          selectedPeriod={filters.selectedPeriod}
          selectedProducts={filters.selectedProducts}
          showCompareToPreviousPeriod={false}
          showCompareToSamePeriodPreviousYear={false}
          showGroupByFilter={false}
          onDateRangeChange={filters.setValueStart}
          onPeriodChange={filters.setSelectedPeriod}
          onProductsChange={filters.setSelectedProducts}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={data} />
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={() => handleExportReport('csv')}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
